/**
 * Session Management Utility
 * Handles session persistence, renewal, and 4-day timeout logic
 */

import { getLocalStorage, setLocalStorage, validators } from './safeJson';
import logger from './logger';

const SESSION_DURATION = 4 * 24 * 60 * 60 * 1000; // 4 days in milliseconds
const RENEWAL_CHECK_INTERVAL = 30 * 60 * 1000; // Check every 30 minutes
const SESSION_STORAGE_KEY = 'condoshield_session';
const ACTIVITY_STORAGE_KEY = 'condoshield_last_activity';

class SessionManager {
  constructor() {
    this.renewalTimer = null;
    this.activityListeners = [];
    this.init();
  }

  init() {
    // Start activity monitoring
    this.startActivityMonitoring();

    // Start periodic session checks
    this.startSessionRenewal();

    // Handle page visibility changes (tab focus)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateActivity();
        // Only check validity if we have session data; avoid forcing reloads when session is absent
        const sessionData = this.getSessionData();
        if (sessionData) {
          this.checkSessionValidity();
        } else {
          logger.debug('[SessionManager] visibilitychange: no session data — skipping validity check');
        }
      }
    });

    logger.info('🔐 Session Manager initialized - 4 day session timeout');
  }

  /**
   * Record user activity and extend session
   */
  updateActivity() {
    const now = Date.now();

    // Update last activity timestamp
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());

    // Extend session expiry by 4 days from now
    const sessionData = this.getSessionData();
    if (sessionData) {
      sessionData.expiresAt = now + SESSION_DURATION;
      sessionData.lastActivity = now;
      setLocalStorage(SESSION_STORAGE_KEY, sessionData);

      logger.debug('🔄 Session extended to:', new Date(sessionData.expiresAt).toLocaleString());
    }
  }

  /**
   * Initialize session data
   */
  initializeSession(userEmail) {
    const now = Date.now();
    const sessionData = {
      userEmail,
      createdAt: now,
      expiresAt: now + SESSION_DURATION,
      lastActivity: now,
      renewalCount: 0,
    };

    setLocalStorage(SESSION_STORAGE_KEY, sessionData);
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());

    logger.info(
      '🔐 Session initialized for:',
      userEmail,
      'expires:',
      new Date(sessionData.expiresAt).toLocaleString()
    );
    return sessionData;
  }

  /**
   * Get current session data
   */
  getSessionData() {
    return getLocalStorage(
      SESSION_STORAGE_KEY,
      null,
      validators.hasKeys('userEmail', 'expiresAt', 'lastActivity')
    );
  }

  /**
   * Check if session is still valid
   */
  isSessionValid() {
    const sessionData = this.getSessionData();
    if (!sessionData) return false;

    const now = Date.now();
    const isValid = now < sessionData.expiresAt;

    if (!isValid) {
      logger.info('⏰ Session expired at:', new Date(sessionData.expiresAt).toLocaleString());
      this.clearSession();
    }

    return isValid;
  }

  /**
   * Check session validity and handle expiry
   */
  checkSessionValidity() {
    if (!this.isSessionValid()) {
      logger.info('🚪 Session expired - user needs to re-authenticate');
      this.clearSession();
      // Emit a custom event instead of forcing a full reload so the app can decide how to handle it
      try {
        const evt = new CustomEvent('condoshield:sessionExpired', { detail: { when: Date.now() } });
        window.dispatchEvent(evt);
        logger.debug('[SessionManager] dispatched condoshield:sessionExpired event');
      } catch (err) {
        logger.warn('[SessionManager] session expired - unable to dispatch event:', err);
      }
      return false;
    }
    return true;
  }

  /**
   * Clear session data
   */
  clearSession() {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    logger.info('🧹 Session data cleared');
  }

  /**
   * Start monitoring user activity
   */
  startActivityMonitoring() {
    const activities = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus',
    ];

    const throttledUpdate = this.throttle(() => {
      this.updateActivity();
    }, 60000); // Throttle to once per minute

    activities.forEach(activity => {
      document.addEventListener(activity, throttledUpdate, { passive: true });
      this.activityListeners.push(() => {
        document.removeEventListener(activity, throttledUpdate);
      });
    });
  }

  /**
   * Start periodic session renewal checks
   */
  startSessionRenewal() {
    this.renewalTimer = setInterval(() => {
      this.checkSessionValidity();
    }, RENEWAL_CHECK_INTERVAL);
  }

  /**
   * Stop session management
   */
  stop() {
    if (this.renewalTimer) {
      clearInterval(this.renewalTimer);
      this.renewalTimer = null;
    }

    // Remove activity listeners
    this.activityListeners.forEach(removeListener => removeListener());
    this.activityListeners = [];
  }

  /**
   * Get session info for debugging
   */
  getSessionInfo() {
    const sessionData = this.getSessionData();
    if (!sessionData) return { status: 'No session' };

    const now = Date.now();
    const timeUntilExpiry = sessionData.expiresAt - now;
    const hoursUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60 * 60));
    const minutesUntilExpiry = Math.floor((timeUntilExpiry % (1000 * 60 * 60)) / (1000 * 60));

    return {
      status: 'Active',
      userEmail: sessionData.userEmail,
      createdAt: new Date(sessionData.createdAt).toLocaleString(),
      expiresAt: new Date(sessionData.expiresAt).toLocaleString(),
      lastActivity: new Date(sessionData.lastActivity).toLocaleString(),
      timeUntilExpiry: `${hoursUntilExpiry}h ${minutesUntilExpiry}m`,
      renewalCount: sessionData.renewalCount || 0,
    };
  }

  /**
   * Throttle function to limit how often a function can be called
   */
  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

// Expose session info to console for debugging
window.getSessionInfo = () => {
  const info = sessionManager.getSessionInfo();
  logger.info('📊 Session Info:', info);
  return info;
};

export default sessionManager;
